// filepath: c:\LAVORO\CF\gestionale-anziani-new\frontend\src\pages\CalendarPage.js
import { useState, useEffect, useCallback } from "react";
import { Box, Typography, TextField, Toolbar, ToggleButtonGroup, ToggleButton, Paper, Chip, useTheme, useMediaQuery } from "@mui/material";
import { FormDialog } from "../components/StandardDialog";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import itLocale from '@fullcalendar/core/locales/it';
import EventIcon from '@mui/icons-material/Event';
import BuildIcon from '@mui/icons-material/Build';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { useRef } from "react";

const CalendarPage = () => {
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isInitialMount = useRef(true);
    const [allEvents, setAllEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [calendarFilter, setCalendarFilter] = useState('all'); // 'all', 'appointments', 'events', 'maintenance'
    const [openDialog, setOpenDialog] = useState(false);
    const [newEvent, setNewEvent] = useState({ 
        type: "", 
        appointment_date: "",
        description: "",
        elderly_id: 1, // Default value, should be selectable in a real app
        doctor_id: 1   // Default value, should be selectable in a real app
    });

    // Funzione per caricare i dati del calendario
    const loadCalendarData = useCallback((startDate, endDate) => {
        // Guard: non caricare se non c'è il token
        if (!auth?.accessToken) {
            return;
        }
        
        const loadAppointments = axiosPrivate.get('/appointments')
            .then(response => {
                const appointments = Array.isArray(response.data) ? response.data : (response.data.appointments || []);
                return appointments.map(apt => {
                    // Parse la data e ora
                    const date = apt.appointment_date.split('T')[0];
                    const startTime = apt.start_time || apt.appointment_time;
                    const [hours, minutes] = startTime.split(':');
                    const endTime = `${String((parseInt(hours) + 1) % 24).padStart(2, '0')}:${minutes}`;
                    
                    return {
                        id: `apt-${apt.appointment_id}`,
                        title: `📅 ${startTime} - ${apt.elderly_name}${apt.n_room ? ` (${apt.n_room})` : ''}`,
                        start: `${date}T${startTime}`,
                        end: `${date}T${endTime}`,
                        backgroundColor: '#1976d2',
                        borderColor: '#1565c0',
                        extendedProps: {
                            type: 'appointment',
                            appointment_id: apt.appointment_id,
                            elderly_name: apt.elderly_name,
                            elderly_id: apt.elderly_id,
                            n_room: apt.n_room,
                            appointment_type: apt.appointment_type,
                            status: apt.status,
                            doctor_name: apt.doctor_name,
                            external_doctor_name: apt.external_doctor_name,
                            family_member_name: apt.family_member_name,
                            external_family_member_name: apt.external_family_member_name,
                            description: apt.description,
                            notes: apt.notes
                        }
                    };
                });
            })
            .catch(error => {
                console.error('Errore nel caricamento appuntamenti:', error);
                return [];
            });

        const loadEvents = axiosPrivate.get('/events')
            .then(response => {
                const events = Array.isArray(response.data) ? response.data : (response.data.events || []);
                return events.map(evt => {
                    const date = evt.event_date.split('T')[0];
                    const startTime = evt.event_time || '09:00';
                    const [hours, minutes] = startTime.split(':');
                    const duration = evt.duration || 120; // Default 2 ore
                    const endHours = parseInt(hours) + Math.floor(duration / 60);
                    const endMinutes = parseInt(minutes) + (duration % 60);
                    const endTime = `${String(endHours % 24).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
                    
                    return {
                        id: `evt-${evt.event_id}`,
                        title: `${startTime} ${evt.title}`,
                        start: `${date}T${startTime}`,
                        end: `${date}T${endTime}`,
                        backgroundColor: '#ff9800',
                        borderColor: '#f57c00',
                        extendedProps: {
                            type: 'event',
                            event_id: evt.event_id,
                            event_type: evt.event_type,
                            location: evt.location,
                            organizer_id: evt.organizer_id,
                            organizer_name: evt.organizer_name,
                            max_participants: evt.max_participants,
                            registered_count: evt.registered_count,
                            status: evt.status,
                            description: evt.description,
                            notes: evt.notes
                        }
                    };
                });
            })
            .catch(error => {
                console.error('Errore nel caricamento eventi:', error);
                return [];
            });

        const loadMaintenance = axiosPrivate.get('/maintenance/requests/scheduled')
            .then(response => {
                const maintenance = Array.isArray(response.data) ? response.data : (response.data.requests || []);
                return maintenance.map(maint => {
                    const date = maint.scheduled_date.split('T')[0];
                    const startTime = maint.scheduled_time || '09:00';
                    const [hours, minutes] = startTime.split(':');
                    const endTime = `${String((parseInt(hours) + 2) % 24).padStart(2, '0')}:${minutes}`;
                    
                    // Colore basato sulla priorità
                    let backgroundColor, borderColor;
                    switch(maint.priority) {
                        case 'urgente':
                            backgroundColor = '#d32f2f';
                            borderColor = '#c62828';
                            break;
                        case 'alta':
                            backgroundColor = '#f57c00';
                            borderColor = '#ef6c00';
                            break;
                        case 'media':
                            backgroundColor = '#fbc02d';
                            borderColor = '#f9a825';
                            break;
                        case 'bassa':
                            backgroundColor = '#388e3c';
                            borderColor = '#2e7d32';
                            break;
                        default:
                            backgroundColor = '#757575';
                            borderColor = '#616161';
                    }
                    
                    return {
                        id: `maint-${maint.request_id}`,
                        title: `🔧 ${startTime} ${maint.title}`,
                        start: `${date}T${startTime}`,
                        end: `${date}T${endTime}`,
                        backgroundColor,
                        borderColor,
                        extendedProps: {
                            type: 'maintenance',
                            request_id: maint.request_id,
                            maintenance_type: maint.maintenance_type,
                            category: maint.category,
                            priority: maint.priority,
                            status: maint.status,
                            location: maint.location,
                            room_number: maint.room_number,
                            company_name: maint.company_name,
                            description: maint.description,
                            estimated_cost: maint.estimated_cost,
                            is_recurring: maint.is_recurring,
                            recurrence_pattern: maint.recurrence_pattern
                        }
                    };
                });
            })
            .catch(error => {
                console.error('Errore nel caricamento manutenzioni:', error);
                return [];
            });

        Promise.all([loadAppointments, loadEvents, loadMaintenance])
            .then(([appointments, events, maintenance]) => {
                const combined = [...appointments, ...events, ...maintenance];
                setAllEvents(combined);
            })
            .catch(error => {
                console.error('❌ Errore nel caricamento dati calendario:', error);
            });
    }, [axiosPrivate, auth?.accessToken]);

    // Carica i dati al mount del componente (solo se autenticato)
    useEffect(() => {
        if (auth?.accessToken) {
            loadCalendarData();
        }
    }, [loadCalendarData, auth?.accessToken]);

    // Applica filtro quando cambia
    useEffect(() => {
        if (calendarFilter === 'all') {
            setFilteredEvents(allEvents);
        } else if (calendarFilter === 'appointments') {
            setFilteredEvents(allEvents.filter(e => e.extendedProps.type === 'appointment'));
        } else if (calendarFilter === 'events') {
            setFilteredEvents(allEvents.filter(e => e.extendedProps.type === 'event'));
        } else if (calendarFilter === 'maintenance') {
            setFilteredEvents(allEvents.filter(e => e.extendedProps.type === 'maintenance'));
        }
    }, [calendarFilter, allEvents]);

    // Gestisce l'apertura del dialog
    const handleDateClick = (info) => {
        setNewEvent({ ...newEvent, appointment_date: info.dateStr + 'T09:00:00' });
        setOpenDialog(true);
    };

    // Gestisce la chiusura del dialog
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setNewEvent({ 
            type: "", 
            appointment_date: "",
            description: "",
            elderly_id: 1,
            doctor_id: 1
        });
    };

    // Salva un nuovo appuntamento nel database
    const handleSaveEvent = () => {
        axiosPrivate.post('/appointments', newEvent)
            .then((res) => {
                // Ricarica tutti gli eventi
                window.location.reload();
            })
            .catch(err => console.error("Errore nell'aggiunta dell'appuntamento:", err));
    };

    // Gestisce il cambio di filtro
    const handleFilterChange = (event, newFilter) => {
        if (newFilter !== null) {
            setCalendarFilter(newFilter);
        }
    };

    // Conta gli eventi per tipo
    const countByType = {
        appointments: allEvents.filter(e => e.extendedProps.type === 'appointment').length,
        events: allEvents.filter(e => e.extendedProps.type === 'event').length,
        maintenance: allEvents.filter(e => e.extendedProps.type === 'maintenance').length
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Toolbar />
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 2,
                gap: 2
            }}>
                <Typography variant={isMobile ? "h5" : "h4"}>
                    Calendario Completo
                </Typography>
                
                {/* Statistiche */}
                <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 1, sm: 2 },
                    flexWrap: 'wrap',
                    width: { xs: '100%', sm: 'auto' }
                }}>
                    <Chip 
                        icon={<MedicalServicesIcon />} 
                        label={isMobile ? `${countByType.appointments}` : `${countByType.appointments} Appuntamenti`}
                        color="primary"
                        variant={calendarFilter === 'appointments' ? 'filled' : 'outlined'}
                        size={isMobile ? "small" : "medium"}
                    />
                    <Chip 
                        icon={<EventIcon />} 
                        label={isMobile ? `${countByType.events}` : `${countByType.events} Eventi`}
                        color="secondary"
                        variant={calendarFilter === 'events' ? 'filled' : 'outlined'}
                        size={isMobile ? "small" : "medium"}
                    />
                    <Chip 
                        icon={<BuildIcon />} 
                        label={isMobile ? `${countByType.maintenance}` : `${countByType.maintenance} Manutenzioni`}
                        color="warning"
                        variant={calendarFilter === 'maintenance' ? 'filled' : 'outlined'}
                        size={isMobile ? "small" : "medium"}
                    />
                </Box>
            </Box>

            {/* Filtri */}
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
                <ToggleButtonGroup
                    value={calendarFilter}
                    exclusive
                    onChange={handleFilterChange}
                    aria-label="filtro calendario"
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    orientation={isMobile ? "vertical" : "horizontal"}
                >
                    <ToggleButton value="all" aria-label="tutti">
                        Tutti ({allEvents.length})
                    </ToggleButton>
                    <ToggleButton value="appointments" aria-label="appuntamenti">
                        <MedicalServicesIcon sx={{ mr: 1 }} />
                        {isMobile ? "Appuntamenti" : `Solo Appuntamenti`} ({countByType.appointments})
                    </ToggleButton>
                    <ToggleButton value="events" aria-label="eventi">
                        <EventIcon sx={{ mr: 1 }} />
                        {isMobile ? "Eventi" : `Solo Eventi`} ({countByType.events})
                    </ToggleButton>
                    <ToggleButton value="maintenance" aria-label="manutenzioni">
                        <BuildIcon sx={{ mr: 1 }} />
                        {isMobile ? "Manutenzioni" : `Solo Manutenzioni`} ({countByType.maintenance})
                    </ToggleButton>
                </ToggleButtonGroup>
            </Paper>

            <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView={isMobile ? "timeGridDay" : "dayGridMonth"}
                headerToolbar={{
                    left: isMobile ? 'prev,next' : 'prev,next today',
                    center: 'title',
                    right: isMobile ? 'today' : 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                contentHeight={isMobile ? 500 : "auto"}
                events={filteredEvents}
                datesSet={(dateInfo) => {
                    // Ignora la prima chiamata al mount (FullCalendar lo chiama prima che il token sia pronto)
                    if (isInitialMount.current) {
                        isInitialMount.current = false;
                        return;
                    }
                    
                    // Guard: non caricare se non c'è il token
                    if (!auth?.accessToken) return;
                    
                    // Ricarica i dati quando l'utente cambia mese/settimana/giorno
                    loadCalendarData(dateInfo.start, dateInfo.end);
                }}
                dateClick={handleDateClick}
                eventClick={(info) => {
                    // Mostra dettagli appropriati per tipo
                    const props = info.event.extendedProps;
                    
                    if (props.type === 'appointment') {
                        alert(
                            `📅 APPUNTAMENTO\n\n` +
                            `Anziano: ${props.elderly_name}${props.n_room ? ` (Camera ${props.n_room})` : ''}\n` +
                            `Orario: ${info.event.start.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})} - ${info.event.end.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}\n` +
                            `Tipo: ${props.appointment_type}\n` +
                            `Stato: ${props.status}\n` +
                            `${props.doctor_name ? `Dottore: ${props.doctor_name}\n` : ''}` +
                            `${props.external_doctor_name ? `Dottore esterno: ${props.external_doctor_name}\n` : ''}` +
                            `${props.family_member_name ? `Familiare: ${props.family_member_name}\n` : ''}` +
                            `${props.external_family_member_name ? `Parente esterno: ${props.external_family_member_name}\n` : ''}` +
                            `${props.description ? `\nDescrizione: ${props.description}` : ''}` +
                            `${props.notes ? `\nNote: ${props.notes}` : ''}`
                        );
                    } else if (props.type === 'event') {
                        alert(
                            `🎉 EVENTO\n\n` +
                            `Titolo: ${info.event.title.replace(/^\d{2}:\d{2}\s/, '')}\n` +
                            `Tipo: ${props.event_type}\n` +
                            `Orario: ${info.event.start.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})} - ${info.event.end.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}\n` +
                            `Luogo: ${props.location || 'N/D'}\n` +
                            `Organizzatore: ${props.organizer_name || 'N/D'}\n` +
                            `${props.max_participants ? `Posti: ${props.registered_count || 0}/${props.max_participants}\n` : ''}` +
                            `Stato: ${props.status}\n` +
                            `${props.description ? `\nDescrizione: ${props.description}` : ''}` +
                            `${props.notes ? `\nNote: ${props.notes}` : ''}`
                        );
                    } else if (props.type === 'maintenance') {
                        const priorityEmoji = {
                            'urgente': '🔴',
                            'alta': '🟠',
                            'media': '🟡',
                            'bassa': '🟢'
                        };
                        const categoryEmoji = {
                            'idraulica': '💧',
                            'elettrica': '⚡',
                            'riscaldamento': '🔥',
                            'climatizzazione': '❄️',
                            'serramenti': '🪟',
                            'tinteggiatura': '🎨',
                            'giardino': '🌿',
                            'attrezzature_mediche': '🏥',
                            'pulizie': '🧹',
                            'disinfestazione': '🐛',
                            'altro': '🔧'
                        };
                        
                        alert(
                            `🔧 MANUTENZIONE\n\n` +
                            `${categoryEmoji[props.category] || '🔧'} ${info.event.title.replace('🔧 ', '').replace(/^\d{2}:\d{2}\s/, '')}\n` +
                            `Tipo: ${props.maintenance_type === 'straordinaria' ? 'Straordinaria' : 'Programmata'}${props.is_recurring ? ' (Ricorrente)' : ''}\n` +
                            `Categoria: ${props.category}\n` +
                            `Priorità: ${priorityEmoji[props.priority] || ''} ${props.priority}\n` +
                            `Stato: ${props.status}\n` +
                            `Orario: ${info.event.start.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})} - ${info.event.end.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}\n` +
                            `Luogo: ${props.location}${props.room_number ? ` - Stanza ${props.room_number}` : ''}\n` +
                            `${props.company_name ? `Manutentore: ${props.company_name}\n` : 'Manutentore: Da assegnare\n'}` +
                            `${props.estimated_cost ? `Preventivo: €${props.estimated_cost}\n` : ''}` +
                            `${props.description ? `\nDescrizione: ${props.description}` : ''}` +
                            `${props.recurrence_pattern ? `\nRicorrenza: ${props.recurrence_pattern}` : ''}`
                        );
                    }
                }}
                locale={itLocale}
                slotMinTime="06:00:00"
                slotMaxTime="22:00:00"
                allDaySlot={false}
                height="auto"
                eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: false
                }}
            />

            {/* Dialog per aggiungere un nuovo evento */}
            <FormDialog
                open={openDialog}
                onClose={handleCloseDialog}
                onSave={handleSaveEvent}
                title="Aggiungi Appuntamento"
                saveText="Salva"
                saveDisabled={!newEvent.type || !newEvent.appointment_date}
            >
                <TextField
                    fullWidth
                    label="Tipo Appuntamento"
                    value={newEvent.type}
                    onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                    sx={{ mb: 2 }}
                />
                <TextField
                    fullWidth
                    label="Data e Ora"
                    type="datetime-local"
                    value={newEvent.appointment_date}
                    onChange={(e) => setNewEvent({ ...newEvent, appointment_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                />
                <TextField
                    fullWidth
                    label="Descrizione"
                    multiline
                    rows={3}
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                />
            </FormDialog>
        </Box>
    );
};

export default CalendarPage;